/**
 * Route chunk prefetching.
 *
 * Every page in Routes.tsx is `React.lazy(() => import(...))`, so the FIRST
 * visit to a route downloads its JS chunk on-click — that download is what
 * triggers the <Suspense> fallback (the loading flash users see on navigation).
 *
 * This warms every page chunk in the background once the app is idle. After a
 * few seconds of warmup, navigating to any page resolves its chunk from the
 * module cache synchronously — Suspense never shows a fallback, so navigation
 * feels instant with no spinner / skeleton at all.
 *
 * Vite turns `import.meta.glob` into the exact same dynamic-import chunks the
 * lazy() calls produce, so triggering them here populates the same cache.
 */

// Eager:false → returns a map of path -> () => import(path). Triggering the
// function downloads (but does not execute) the chunk.
const pageModules = import.meta.glob([
  "/src/pages/**/*.{tsx,ts}",
  "/src/kb/**/*.{tsx,ts}",
]);

let started = false;

export function prefetchAllRoutes(): void {
  if (started) return;
  started = true;

  const loaders = Object.values(pageModules);

  // Stagger so the prefetch never competes with the data fetches of the page
  // the user is actually looking at — a few chunks per idle tick.
  let i = 0;
  const BATCH = 6;

  function pump() {
    if (i >= loaders.length) return;
    const batch = loaders.slice(i, i + BATCH);
    i += BATCH;
    Promise.all(batch.map((load) => load().catch(() => {}))).then(() => {
      schedule(pump);
    });
  }

  schedule(pump);
}

function schedule(fn: () => void): void {
  const ric = (window as any).requestIdleCallback as
    | ((cb: () => void, opts?: { timeout: number }) => void)
    | undefined;
  if (ric) ric(fn, { timeout: 2000 });
  else window.setTimeout(fn, 200);
}
